import { useCallback, useEffect, useState } from "react";
import { useSendTransaction } from "wagmi";
import { Loader2, Receipt, Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { TxStatus, runTx } from "./TxStatus";

// Mainnet launch + profile fee: USD price set by admins, paid in ETH to the treasury and verified on-chain by the backend.
export const useLaunchFee = (chainId, user) => {
  const [s, setS] = useState({ loading: true });
  const load = useCallback(async () => {
    if (chainId !== 4663 || !user) return setS({ loading: false, required: false });
    try {
      const [f, p] = await Promise.all([api.get("/launch/fee"), api.get("/launch/fee/paid")]);
      setS({ loading: false, ...f.data, paid: p.data });
    } catch (e) { setS({ loading: false, required: true, unavailable: errMsg(e) }); }
  }, [chainId, user]);
  useEffect(() => { load(); }, [load]);
  return { ...s, ready: !s.loading && (!s.required || !!s.paid), reload: load, setPaid: (paid) => setS((x) => ({ ...x, paid })) };
};

const confirmPayment = async (id, hash) => {
  for (let i = 0; i < 8; i++) {
    try { return (await api.post(`/launch/fee/${id}/pay`, { tx_hash: hash })).data; }
    catch (e) { if (!/not found or not yet confirmed/i.test(errMsg(e)) || i === 7) throw e; await new Promise((r) => setTimeout(r, 3000)); }
  }
};

export const LaunchFeeStep = ({ fee, pc }) => {
  const { sendTransactionAsync } = useSendTransaction();
  const [tx, setTx] = useState(null);
  const [busy, setBusy] = useState(false);
  if (fee.loading || !fee.required) return null;
  if (fee.paid) return <p data-testid="launch-fee-paid" className="flex items-center gap-2 text-xs text-lime"><Check className="h-4 w-4" /> Launch & profile fee paid (${fee.paid.required_usd}) — ready to deploy.</p>;
  if (fee.unavailable) return <p data-testid="launch-fee-unavailable" className="text-xs text-danger">{fee.unavailable}</p>;
  const pay = async () => {
    setBusy(true);
    try {
      const q = (await api.post("/launch/fee/quote")).data;
      const rc = await runTx({ pc, setTx, chainId: 4663, label: `Pay launch fee ($${q.required_usd})`, send: () => sendTransactionAsync({ chainId: 4663, to: q.treasury, value: BigInt(q.required_wei) }) });
      if (!rc) return;
      setTx({ stage: "pending", label: "Verifying payment on-chain", hash: rc.transactionHash, chainId: 4663 });
      await confirmPayment(q.id, rc.transactionHash);
      setTx({ stage: "confirmed", label: "Launch fee verified", hash: rc.transactionHash, chainId: 4663 });
      fee.setPaid({ id: q.id, tx_hash: rc.transactionHash, required_usd: q.required_usd });
    } catch (e) { setTx((t) => ({ ...t, stage: "error", label: "Launch fee", error: errMsg(e) })); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="launch-fee-step" className="rounded-lg border border-lime/25 bg-lime/[0.04] p-4 space-y-3">
      <p className="text-sm text-slate-200"><b className="text-white">Step 1 · Launch & profile fee:</b> {fee.discount && <span data-testid="launch-fee-discount" className="mr-1 rounded bg-lime/15 px-1.5 text-lime">{fee.discount}: <s className="text-slate-500">${fee.standard_usd}</s></span>}${fee.required_usd} ≈ <span className="font-mono text-lime">{fee.required_eth} ETH</span> at ${Number(fee.eth_usd).toFixed(2)}/ETH, sent from your wallet to the HIVEVORA treasury <span className="font-mono">{fee.treasury.slice(0, 6)}…{fee.treasury.slice(-4)}</span>. Covers your token's HIVEVORA profile & listing. The ETH amount is locked for 30 minutes when you click pay.</p>
      <button data-testid="launch-fee-pay-button" disabled={busy} onClick={pay} className="btn-lime px-5 py-2.5 text-sm disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} {busy ? "Processing…" : `Pay $${fee.required_usd} launch fee`}
      </button>
      <TxStatus tx={tx} testid="launch-fee-tx-status" />
    </div>
  );
};
